import React, {useEffect, useState, useRef} from "react";
import {toAbsoluteUrl} from "../../../_metronic/_helpers";
import axios from "axios";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import alertify from "alertifyjs";
import {API_URL} from "../constants.js";

import {
    Card,
    CardBody,
    CardHeader,
    CardHeaderToolbar,
} from "../../../_metronic/_partials/controls";
import {Link} from "react-router-dom";
import {Table} from "react-bootstrap";
import SVG from "react-inlinesvg";
import {useTranslation} from "react-i18next";

export function BiomarkerCreate(props) {
    const {t} = useTranslation();

    const name = useRef();
    const machine_code = useRef();
    const machine_id = useRef();
    const unit_id = useRef();
    const default_result = useRef();
    const display_order = useRef();

    const [units, setUnits] = useState([]);
    const [machines, setMachines] = useState([]);
    const [disabled, setDisabled] = useState(0);
    const [iconDisabled, setIconDisabled] = useState(1);

    // Normal Values State
    const initialNormalValue = {
        "gender": "default",
        "from_age": 0,
        "to_age": 120,
        "minimum_value": 0,
        "maximum_value": 0,
        "label": "",
        "borderline_type": "",
        "borderline_value": "",
        "low_text": "",
        "high_text": "",
        "borderline_text": "",
        "normal_text": ""
    };
    const [normalValues, setNormalValues] = useState([{...initialNormalValue}]);

    useEffect(() => {
        axios.get(API_URL + 'biomarker/load-prerequisites').then((response) => {
            if (response.data.status) {
                setUnits(response.data.units || []);
                setMachines(response.data.machines || []);
            } else {
                alertify.error(response.data.message);
                return null;
            }
        }).catch((error) => {
            alertify.error("Something went wrong. Try again!");
        });
    }, []);

    const addMoreNormalValue = () => {
        setNormalValues([...normalValues, {...initialNormalValue}]);
    }

    const removeNormalValue = (index) => {
        setNormalValues(normalValues.filter((elem, ind) => ind !== index));
    }

    const updateNormalValue = (index, field, value) => {
        const updated = [...normalValues];
        updated[index][field] = value;
        setNormalValues(updated);
    }

    const createBiomarkerHandler = (e) => {
        e.preventDefault();
        
        if (!name.current.value.trim()) {
            alertify.error("Biomarker name is required");
            return;
        }
        if (!machine_code.current.value.trim()) {
            alertify.error("Biomarker machine code is required");
            return;
        }

        setDisabled(1);
        setIconDisabled(0);

        axios.post(API_URL + "biomarker/create", {
            name: name.current.value,
            machine_code: machine_code.current.value.trim(),
            machine_id: machine_id.current.value || null,
            unit_id: unit_id.current.value,
            default_result: default_result.current.value,
            display_order: display_order.current.value || 0,
            normal_ranges: normalValues
        }).then((response) => {
            setDisabled(0);
            setIconDisabled(1);
            if (response.data.status) {
                alertify.success(response.data.message);
                props.history.push("/biomarker/list");
            } else {
                alertify.error(response.data.message);
                return null;
            }
        }).catch((error) => {
            setDisabled(0);
            setIconDisabled(1);
            alertify.error("Something went wrong. Try again!");
        });
    }

    return (
        <>
            <Row>
                <Col md={12}>
                    <Card>
                        <CardHeader title={t("Create New Biomarker")}>
                            <CardHeaderToolbar>
                                <Link to="/biomarker/list" className="btn btn-primary btn-sm mx-3">
                                    {t("Biomarker List")}
                                </Link>
                            </CardHeaderToolbar>
                        </CardHeader>
                        <CardBody>
                            <div className="mt-3">
                                <Form onSubmit={createBiomarkerHandler}>
                                    {/* Basic Information */}
                                    <h5 className="mb-3">{t("Basic Information")}</h5>
                                    <Form.Row>
                                        <Form.Group as={Col} md={4}>
                                            <Form.Label>{t("Biomarker Name")} <span className="text-danger">*</span></Form.Label>
                                            <Form.Control ref={name} placeholder={t("Enter Biomarker Name")} required/>
                                        </Form.Group>
                                        <Form.Group as={Col} md={4}>
                                            <Form.Label>{t("Biomarker machine code")} <span className="text-danger">*</span></Form.Label>
                                            <Form.Control ref={machine_code} placeholder={t("Enter Biomarker machine code")} required/>
                                        </Form.Group>
                                        <Form.Group as={Col} md={4}>
                                            <Form.Label>{t("Machine")}</Form.Label>
                                            <Form.Control as="select" ref={machine_id}>
                                                <option value="">{t("Select Machine")}</option>
                                                {machines.length > 0 ? (
                                                    machines.map((machine, index) => (
                                                        <option key={index} value={machine.id}>
                                                            {machine.short_code ? `${machine.short_code} (${machine.name})` : machine.name}
                                                        </option>
                                                    ))
                                                ) : null}
                                            </Form.Control>
                                        </Form.Group>
                                        <Form.Group as={Col} md={4}>
                                            <Form.Label>{t("Unit")}</Form.Label>
                                            <Form.Control as="select" ref={unit_id}>
                                                <option value="">{t("Select Unit")}</option>
                                                {units.length > 0 ? (
                                                    units.map((unit, index) => (
                                                        <option key={index} value={unit.id}>{unit.name}</option>
                                                    ))
                                                ) : null}
                                            </Form.Control>
                                        </Form.Group>
                                    </Form.Row>
                                    <Form.Row>
                                        <Form.Group as={Col} md={4}>
                                            <Form.Label>{t("Default Result")}</Form.Label>
                                            <Form.Control ref={default_result} placeholder={t("Enter Default Result")}/>
                                        </Form.Group>
                                        <Form.Group as={Col} md={4}>
                                            <Form.Label>{t("Display Order")}</Form.Label>
                                            <Form.Control ref={display_order} type="number" placeholder={t("Enter Display Order")} defaultValue={0}/>
                                        </Form.Group>
                                    </Form.Row>

                                    <hr/>

                                    {/* Normal Ranges Section */}
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <h5 className="mb-0">{t("Normal Ranges")}</h5>
                                        <Button variant="success" size="sm" onClick={addMoreNormalValue}>
                                            <i className="fas fa-plus"></i> {t("Add Normal Range")}
                                        </Button>
                                    </div>

                                    {normalValues.length > 0 ? (
                                        normalValues.map((normalValue, index) => (
                                            <Card key={index} className="mb-3 border">
                                                <CardBody>
                                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                                        <strong>{t("Range")} #{index + 1}</strong>
                                                        {normalValues.length > 1 && (
                                                            <Button 
                                                                variant="outline-danger" 
                                                                size="sm"
                                                                onClick={() => removeNormalValue(index)}
                                                            >
                                                                <i className="fas fa-trash"></i> {t("Remove")}
                                                            </Button>
                                                        )}
                                                    </div>
                                                    <Form.Row>
                                                        <Form.Group as={Col} md={2}>
                                                            <Form.Label>{t("Gender")}</Form.Label>
                                                            <Form.Control 
                                                                as="select"
                                                                value={normalValue.gender}
                                                                onChange={(e) => updateNormalValue(index, 'gender', e.target.value)}
                                                            >
                                                                <option value="default">{t("Default")}</option>
                                                                <option value="male">{t("Male")}</option>
                                                                <option value="female">{t("Female")}</option>
                                                                <option value="na">{t("Not Answered")}</option>
                                                            </Form.Control>
                                                        </Form.Group>
                                                        <Form.Group as={Col} md={2}>
                                                            <Form.Label>{t("From Age")}</Form.Label>
                                                            <Form.Control 
                                                                type="number"
                                                                value={normalValue.from_age}
                                                                onChange={(e) => updateNormalValue(index, 'from_age', e.target.value)}
                                                            />
                                                        </Form.Group>
                                                        <Form.Group as={Col} md={2}>
                                                            <Form.Label>{t("To Age")}</Form.Label>
                                                            <Form.Control 
                                                                type="number"
                                                                value={normalValue.to_age}
                                                                onChange={(e) => updateNormalValue(index, 'to_age', e.target.value)}
                                                            />
                                                        </Form.Group>
                                                        <Form.Group as={Col} md={2}>
                                                            <Form.Label>{t("Min Value")}</Form.Label>
                                                            <Form.Control 
                                                                type="number"
                                                                step="0.01"
                                                                value={normalValue.minimum_value}
                                                                onChange={(e) => updateNormalValue(index, 'minimum_value', e.target.value)}
                                                            />
                                                        </Form.Group>
                                                        <Form.Group as={Col} md={2}>
                                                            <Form.Label>{t("Max Value")}</Form.Label>
                                                            <Form.Control 
                                                                type="number"
                                                                step="0.01"
                                                                value={normalValue.maximum_value}
                                                                onChange={(e) => updateNormalValue(index, 'maximum_value', e.target.value)}
                                                            />
                                                        </Form.Group>
                                                        <Form.Group as={Col} md={2}>
                                                            <Form.Label>{t("Label")}</Form.Label>
                                                            <Form.Control 
                                                                type="text"
                                                                value={normalValue.label}
                                                                onChange={(e) => updateNormalValue(index, 'label', e.target.value)}
                                                                placeholder={t("e.g., Normal, High")}
                                                            />
                                                        </Form.Group>
                                                    </Form.Row>
                                                    <Form.Row>
                                                        <Form.Group as={Col} md={3}>
                                                            <Form.Label>{t("Borderline Type")}</Form.Label>
                                                            <Form.Control 
                                                                as="select"
                                                                value={normalValue.borderline_type}
                                                                onChange={(e) => updateNormalValue(index, 'borderline_type', e.target.value)}
                                                            >
                                                                <option value="">{t("None")}</option>
                                                                <option value="low">{t("Low")}</option>
                                                                <option value="high">{t("High")}</option>
                                                            </Form.Control>
                                                        </Form.Group>
                                                        <Form.Group as={Col} md={3}>
                                                            <Form.Label>{t("Borderline Value")}</Form.Label>
                                                            <Form.Control 
                                                                type="number"
                                                                step="0.01"
                                                                value={normalValue.borderline_value}
                                                                onChange={(e) => updateNormalValue(index, 'borderline_value', e.target.value)}
                                                            />
                                                        </Form.Group>
                                                    </Form.Row>
                                                    <Form.Row>
                                                        <Form.Group as={Col} md={3}>
                                                            <Form.Label>{t("Low Text")}</Form.Label>
                                                            <Form.Control 
                                                                as="textarea"
                                                                rows={2}
                                                                value={normalValue.low_text}
                                                                onChange={(e) => updateNormalValue(index, 'low_text', e.target.value)}
                                                            />
                                                        </Form.Group>
                                                        <Form.Group as={Col} md={3}>
                                                            <Form.Label>{t("High Text")}</Form.Label>
                                                            <Form.Control 
                                                                as="textarea"
                                                                rows={2}
                                                                value={normalValue.high_text}
                                                                onChange={(e) => updateNormalValue(index, 'high_text', e.target.value)}
                                                            />
                                                        </Form.Group>
                                                        <Form.Group as={Col} md={3}>
                                                            <Form.Label>{t("Borderline Text")}</Form.Label>
                                                            <Form.Control 
                                                                as="textarea"
                                                                rows={2}
                                                                value={normalValue.borderline_text}
                                                                onChange={(e) => updateNormalValue(index, 'borderline_text', e.target.value)}
                                                            />
                                                        </Form.Group>
                                                        <Form.Group as={Col} md={3}>
                                                            <Form.Label>{t("Normal Text")}</Form.Label>
                                                            <Form.Control 
                                                                as="textarea"
                                                                rows={2}
                                                                value={normalValue.normal_text}
                                                                onChange={(e) => updateNormalValue(index, 'normal_text', e.target.value)}
                                                            />
                                                        </Form.Group>
                                                    </Form.Row>
                                                </CardBody>
                                            </Card>
                                        ))
                                    ) : (
                                        <p className="text-muted">{t("No normal ranges added. Click 'Add Normal Range' to add one.")}</p>
                                    )}

                                    <hr/>

                                    <Button variant="primary" className="float-right" type="submit" disabled={disabled}>
                                        {t("Save Biomarker")}
                                        <span dangerouslySetInnerHTML={{__html: disabled ? ` <i class='fas fa-spinner fa-spin'></i>` : ``}}/>
                                    </Button>
                                </Form>
                            </div>
                        </CardBody>
                    </Card>
                </Col>
            </Row>
        </>
    );
}
